import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@gigpro/ui/DropdownMenu";
import { Check, ChevronDown, Store } from "@gigpro/ui/Icons";
import { cn } from "@gigpro/ui/tailwind/cn";
import { useBreakpoint } from "@gigpro/ui/utils/useBreakpoint";
import { Link } from "@tanstack/react-router";
import { useInheritLocation } from "layouts/BaseLayout/utils";
import { Briefcase } from "lucide-react";

type Mode = "shifts" | "hire";

type ModeSwitcherProps = {
  currentMode: Mode;
  className?: string;
};

const NewBadge = () => (
  <span className="rounded-sm bg-brand px-1 py-0.5 text-extraSmall font-semibold text-white uppercase">New</span>
);

export function ModeSwitcher({ currentMode, className }: Readonly<ModeSwitcherProps>) {
  const inheritLocation = useInheritLocation();
  const products = [
    {
      mode: "shifts",
      label: "Marketplace",
      subtitle: "On-demand shift staffing",
      to: "/locations/$locationId/home",
      Icon: Store,
      isNew: false,
    },
    {
      mode: "hire",
      label: "Hire",
      subtitle: "Job postings, applications & offers",
      to: "/locations/$locationId/hire",
      Icon: Briefcase,
      isNew: true,
    },
  ] as const;

  const current = products.find((product) => product.mode === currentMode) ?? products[0];
  const isDesktop = useBreakpoint("md");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "group flex items-center gap-1.5 rounded-sm bg-[color-mix(in_srgb,var(--color-white)_15%,var(--color-black))] px-1.5 py-1 text-base font-semibold tracking-tight text-white hover:bg-[color-mix(in_srgb,var(--color-white)_20%,var(--color-black))] md:text-large",
          className,
        )}
      >
        <current.Icon className="size-4.5 rounded-sm bg-brand p-1 text-white" />
        <span>{current.label}</span>
        {current.isNew && <NewBadge />}
        <ChevronDown className="mr-0.5 ml-auto size-3.5 transition-transform group-data-[state=open]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent
          collisionPadding={0}
          side={isDesktop ? "bottom" : "top"}
          align={isDesktop ? "start" : "end"}
          className="w-70 max-w-[calc(100vw-var(--spacing)*4)]"
        >
          <DropdownMenuLabel className="text-small tracking-wide uppercase">Switch Product</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={currentMode} className="flex flex-col gap-0.5">
            {products.map(({ mode, label, subtitle, to, Icon, isNew }) => {
              const isCurrent = mode === currentMode;
              const content = (
                <>
                  <Icon className="size-7 shrink-0 rounded-md bg-background p-2 text-black group-data-[state=checked]:bg-brand group-data-[state=checked]:text-white" />
                  <p className="flex flex-col">
                    <span className="flex items-center gap-1.5 text-large font-semibold">
                      {label}
                      {isNew && <NewBadge />}
                    </span>
                    <span className="text-gray">{subtitle}</span>
                  </p>
                  {isCurrent && <Check className="ml-auto size-5 text-brand" />}
                </>
              );
              return (
                <DropdownMenuRadioItem
                  key={mode}
                  value={mode}
                  asChild
                  className="group gap-2.5 p-1.5 text-black not-data-[state=checked]:cursor-pointer hover:bg-background/40 data-[state=checked]:bg-accent-light-green"
                >
                  {isCurrent ? (
                    <div>{content}</div>
                  ) : (
                    <Link to={to} params={inheritLocation}>
                      {content}
                    </Link>
                  )}
                </DropdownMenuRadioItem>
              );
            })}
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <p className="px-2 py-1 text-small font-regular tracking-tight text-gray">
            Your accounts, locations and pros are shared across products.
          </p>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
